import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { logError } from "@faire/web/common/logging";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { useEffect, useState } from "react";

interface IScriptCheck {
  isScriptLoaded: () => boolean;
  loadScript: () => Promise<void>;
  provider: SocialSignOnProvider;
}

export const useScriptCheck = ({
  isScriptLoaded,
  loadScript,
  provider,
}: IScriptCheck) => {
  // Script load error => Should trigger an error message
  const [hasScriptError, setHasScriptError] = useState(false);
  // Script load check
  const isSSR = useIsSSR();
  const [isScriptLoading, setIsScriptLoading] = useState(
    !isSSR && !isScriptLoaded()
  );
  const [didInit, setDidInit] = useState(!isSSR);
  useEffect(() => {
    if (!didInit && !isSSR) {
      // In SSR, wait until after hydration to load the script.
      setIsScriptLoading(!isScriptLoaded());
      setDidInit(true);
    }
  }, [didInit, isSSR, isScriptLoaded]);

  // Load script if needed
  useEffect(() => {
    if (isScriptLoading) {
      loadScript()
        .then(() => setIsScriptLoading(false))
        .catch((error) => {
          setHasScriptError(true);
          setIsScriptLoading(false);
          logError(error, {
            tags: {
              signInType: "SSO",
              code: "script_load_failed",
              provider,
            },
          });
        });
    }
  }, [isScriptLoading, isScriptLoaded, loadScript, provider]);

  return { isScriptLoading, hasScriptError };
};
